/**
 * Ronde 3 — PR-1 Klantafspraken types & Zod-schemas.
 *
 * Bronspec: `docs/specs/ronde-3/01-klantafspraken-consignatie.md` §2.1, §4.2, §5.4.
 *
 * Hier alleen `ClientAgreementRule` + resolved-output. ConsignatieRooster /
 * ConsignatieOproep types volgen in PR-3, factuur-categorie-uitbreidingen
 * in PR-4.
 */

import { z } from 'zod'

// ─── Enums (matchen Prisma — packages/db/prisma/schema.prisma) ───────────

export const AGREEMENT_CATEGORIES = [
  'REISKOSTEN',
  'REISTIJD',
  'UURTARIEF',
  'OVERUURTARIEF',
  'PLOEGENTOESLAG',
  'VAST_BEDRAG',
  'CONSIGNATIE',
  'OVERIG',
  // batch-68 controle (FO-343 §4) - afspraak-gestuurde pauze en uren-grenzen.
  'PAUZE',
  'UREN',
] as const
export type AgreementCategory = (typeof AGREEMENT_CATEGORIES)[number]

export const AGREEMENT_RULE_TYPES = [
  'INBEGREPEN',
  'MAXIMUM',
  'VAST_TARIEF_PER_EENHEID',
  'AFWIJKEND_TARIEF',
  'TOESLAG_PERCENTAGE',
  'TOESLAG_BEDRAG',
  'DAGVERGOEDING',
  'WEEKVERGOEDING',
  'STANDBY_WEEKTARIEF',
  'STANDBY_DAGTARIEF',
  'OPROEP_UURTARIEF',
  'OPROEP_MINIMUMUREN',
  'VRIJE_AFSPRAAK',
  // §6.2 (architectuurbeslissingen 2026-05-18) — twee reistijd-rules:
  //   REISTIJD_BRUTO   = boolean (flag=true) → geen drempel-aftrek voor deze
  //                      klant; alle berekende reistijd is vergoedbaar.
  //   REISTIJD_DREMPEL = value = custom drempel-minuten die override globale
  //                      `REISUREN_DREMPEL_MINUTEN`-instelling.
  'REISTIJD_BRUTO',
  'REISTIJD_DREMPEL',
  // batch-68 controle (FO-343 §4) - de afsprakenmotor (alle default-uit).
  'PAUZE_NIET_FACTUREERBAAR',
  'PAUZE_VOLLEDIG_FACTUREERBAAR',
  'PAUZE_PERCENTAGE',
  'PAUZE_MINUTEN_FACTUREERBAAR',
  'PAUZE_MINUTEN_NIET_FACTUREERBAAR',
  'REISTIJD_PERCENTAGE',
  'MINIMUM_PER_DAG',
  'MAXIMUM_PER_DAG',
  'AFRONDING',
  'VASTE_PRIJS_PERIODE',
] as const
export type AgreementRuleType = (typeof AGREEMENT_RULE_TYPES)[number]

/** batch-68 controle (FO-343 §4.2/§4.3) - het niveau van een begrenzing (REGEL alleen voor AFRONDING). */
export const BEGRENZING_NIVEAUS = ['RIT', 'DAG', 'WEEK', 'MEDEWERKER_PERIODE', 'WERKBON', 'PROJECT', 'REGEL'] as const
export type BegrenzingNiveau = (typeof BEGRENZING_NIVEAUS)[number]

export const BEGRENZING_NIVEAU_LABELS: Record<BegrenzingNiveau, string> = {
  RIT: 'Per rit',
  DAG: 'Per dag',
  WEEK: 'Per week',
  MEDEWERKER_PERIODE: 'Per medewerker per periode',
  WERKBON: 'Per werkbon',
  PROJECT: 'Per project',
  REGEL: 'Per factuurregel',
}

/** De niveaus die een REISTIJD-MAXIMUM kan hebben (REGEL hoort bij afronding). */
export const REISTIJD_BEGRENZING_NIVEAUS = ['RIT', 'DAG', 'WEEK', 'MEDEWERKER_PERIODE', 'WERKBON', 'PROJECT'] as const
export type ReistijdBegrenzingNiveau = (typeof REISTIJD_BEGRENZING_NIVEAUS)[number]

/** De niveaus van een AFRONDING. */
export const AFRONDING_NIVEAUS = ['DAG', 'REGEL'] as const

export const AFRONDING_MODI_TYPES = ['OMHOOG', 'DICHTSTBIJ', 'OMLAAG'] as const
export type AfrondingModusType = (typeof AFRONDING_MODI_TYPES)[number]

export const AFRONDING_MODUS_LABELS: Record<AfrondingModusType, string> = {
  OMHOOG: 'Naar boven',
  DICHTSTBIJ: 'Naar de dichtstbijzijnde eenheid',
  OMLAAG: 'Naar beneden',
}

/** De regelsoorten van de afsprakenmotor (FO-343 §4): zonder deze regels factureert alles als voorheen. */
export const MOTOR_RULE_TYPES: readonly AgreementRuleType[] = [
  'PAUZE_NIET_FACTUREERBAAR',
  'PAUZE_VOLLEDIG_FACTUREERBAAR',
  'PAUZE_PERCENTAGE',
  'PAUZE_MINUTEN_FACTUREERBAAR',
  'PAUZE_MINUTEN_NIET_FACTUREERBAAR',
  'REISTIJD_PERCENTAGE',
  'MINIMUM_PER_DAG',
  'MAXIMUM_PER_DAG',
  'AFRONDING',
  'VASTE_PRIJS_PERIODE',
]

/**
 * batch-68 controle (FO-343 §4) - is dit een afspraak van de motor? De tien
 * motorsoorten, plus een reistijd-maximum MET niveau (zonder niveau is het de oude
 * afspraak, die de factuur niet begrenst).
 */
export function isMotorAfspraak(r: {
  category: AgreementCategory
  ruleType: AgreementRuleType
  niveau: BegrenzingNiveau | null
}): boolean {
  if (MOTOR_RULE_TYPES.includes(r.ruleType)) return true
  return r.category === 'REISTIJD' && r.ruleType === 'MAXIMUM' && r.niveau !== null
}

/**
 * batch-68 controle (FO-343 §4.4/§4.5) - welke scopevelden een afspraak kan hebben.
 * Functie en project kent alleen de motor: de andere paden (werkbon-tarieven,
 * reisuren, consignatie) geven die context niet mee, dus zo'n regel zou daar nooit
 * gelden. Een vaste prijs geldt per periode voor de klant of een project, nooit per
 * medewerker of functie.
 */
export function scopeVeldenVoorAfspraak(r: {
  category: AgreementCategory
  ruleType: AgreementRuleType
  niveau: BegrenzingNiveau | null
}): { medewerker: boolean; functie: boolean; project: boolean } {
  const motor = isMotorAfspraak(r)
  const vastePrijs = r.ruleType === 'VASTE_PRIJS_PERIODE'
  return { medewerker: !vastePrijs, functie: motor && !vastePrijs, project: motor }
}

/**
 * batch-68 controle (FO-343 §4.4, review fase B) - een vaste prijs per periode komt
 * alleen via het facturatie-overzicht op de factuur (één keer per periode), en het
 * overzicht genereren en er een factuur van maken kan in de backoffice nog niet
 * (FO-343 fase C). Tot dan weigert de API een actieve vaste-prijsafspraak en toont
 * het formulier hem niet; de motor rekent hem wel al (getest).
 *
 * Aanzetten = deze vlag op true, plus: een ingang voor overzicht -> factuur, de
 * Finqle-poort (`beoordeelVerzendbaar`) een bon onder een vaste prijs laten weigeren,
 * en een late werkbon of tweede BV in dezelfde periode laten factureren (reis,
 * materiaal en het meerwerkverschil; zie STATUS-controle "PO-beslispunten").
 */
export const VASTE_PRIJS_BESCHIKBAAR: boolean = false

export const VASTE_PRIJS_NIET_BESCHIKBAAR_MELDING =
  'Een vaste prijs per periode kan nog niet actief worden: die komt alleen via het facturatie-overzicht op de factuur, en dat staat nog niet in de backoffice. Leg hem tot dan inactief of als vrije afspraak vast en factureer hem handmatig.'

export const PAUZE_RULE_TYPES = [
  'PAUZE_NIET_FACTUREERBAAR',
  'PAUZE_VOLLEDIG_FACTUREERBAAR',
  'PAUZE_PERCENTAGE',
  'PAUZE_MINUTEN_FACTUREERBAAR',
  'PAUZE_MINUTEN_NIET_FACTUREERBAAR',
] as const
export type PauzeRuleType = (typeof PAUZE_RULE_TYPES)[number]

// Scope-enums hergebruiken bestaande Prisma-enums.
import type { WerkbonType, DienstType, PloegSoort } from './werkbonnen'

// ─── Labels (NL) — spec §5.4 ──────────────────────────────────────────────

export const AGREEMENT_CATEGORY_LABELS: Record<AgreementCategory, string> = {
  REISKOSTEN: 'Reiskosten',
  REISTIJD: 'Reistijd',
  UURTARIEF: 'Uurtarief',
  OVERUURTARIEF: 'Overuurtarief',
  PLOEGENTOESLAG: 'Ploegentoeslag',
  VAST_BEDRAG: 'Vast bedrag',
  CONSIGNATIE: 'Consignatie',
  OVERIG: 'Overig',
  PAUZE: 'Pauze',
  UREN: 'Uren (minimum, maximum, afronding)',
}

export const AGREEMENT_RULE_TYPE_LABELS: Record<AgreementRuleType, string> = {
  INBEGREPEN: 'Inbegrepen in tarief',
  MAXIMUM: 'Gemaximaliseerd',
  VAST_TARIEF_PER_EENHEID: 'Afwijkend tarief per eenheid',
  AFWIJKEND_TARIEF: 'Afwijkend tarief',
  TOESLAG_PERCENTAGE: 'Toeslag (percentage)',
  TOESLAG_BEDRAG: 'Toeslag (vast bedrag)',
  DAGVERGOEDING: 'Dagvergoeding',
  WEEKVERGOEDING: 'Weekvergoeding',
  STANDBY_WEEKTARIEF: 'Standby per week',
  STANDBY_DAGTARIEF: 'Standby per dag',
  OPROEP_UURTARIEF: 'Oproep-uurtarief',
  OPROEP_MINIMUMUREN: 'Minimum uren per oproep',
  VRIJE_AFSPRAAK: 'Vrije afspraak',
  REISTIJD_BRUTO: 'Reistijd bruto (geen drempel)',
  REISTIJD_DREMPEL: 'Eigen reistijd-drempel (minuten)',
  PAUZE_NIET_FACTUREERBAAR: 'Pauze niet factureerbaar',
  PAUZE_VOLLEDIG_FACTUREERBAAR: 'Pauze volledig factureerbaar',
  PAUZE_PERCENTAGE: 'Pauze deels factureerbaar (%)',
  PAUZE_MINUTEN_FACTUREERBAAR: 'Maximaal aantal minuten pauze factureerbaar',
  PAUZE_MINUTEN_NIET_FACTUREERBAAR: 'Aantal minuten pauze niet factureerbaar',
  REISTIJD_PERCENTAGE: 'Reistijd deels factureerbaar (%)',
  MINIMUM_PER_DAG: 'Minimum uren per dag',
  MAXIMUM_PER_DAG: 'Maximum uren per dag',
  AFRONDING: 'Afronding van de uren',
  VASTE_PRIJS_PERIODE: 'Vaste prijs per periode (+ meerwerk)',
}

/**
 * Mapping welke ruleTypes binnen welke categorie zinvol zijn — gebruikt door
 * de UI om de tweede dropdown (regeltype) te filteren ná het kiezen van een
 * category. Sinds batch-68 dwingt de API hem ook af bij elke nieuwe regel en bij
 * een wijziging van categorie of regelsoort (een onbruikbare combinatie werd door de
 * resolver stil genegeerd). Een bestaande oude regel blijft bewerkbaar zolang
 * categorie en soort niet veranderen.
 */
export const RULE_TYPES_BY_CATEGORY: Record<AgreementCategory, AgreementRuleType[]> = {
  REISKOSTEN: ['INBEGREPEN', 'MAXIMUM', 'VAST_TARIEF_PER_EENHEID'],
  // §6.2: REISTIJD-categorie krijgt extra BRUTO + DREMPEL voor klant-overrides
  // van de globale drempel/aftrek-logica (zie reisuren-service).
  REISTIJD: [
    'INBEGREPEN',
    'MAXIMUM',
    'VAST_TARIEF_PER_EENHEID',
    'REISTIJD_BRUTO',
    'REISTIJD_DREMPEL',
    // batch-68 controle (FO-343 §4.2).
    'REISTIJD_PERCENTAGE',
  ],
  UURTARIEF: ['AFWIJKEND_TARIEF', 'TOESLAG_PERCENTAGE', 'TOESLAG_BEDRAG'],
  OVERUURTARIEF: ['AFWIJKEND_TARIEF', 'TOESLAG_PERCENTAGE', 'TOESLAG_BEDRAG'],
  PLOEGENTOESLAG: ['TOESLAG_PERCENTAGE', 'TOESLAG_BEDRAG'],
  VAST_BEDRAG: ['DAGVERGOEDING', 'WEEKVERGOEDING', 'VASTE_PRIJS_PERIODE'],
  CONSIGNATIE: [
    'STANDBY_WEEKTARIEF',
    'STANDBY_DAGTARIEF',
    'OPROEP_UURTARIEF',
    'OPROEP_MINIMUMUREN',
  ],
  OVERIG: ['VRIJE_AFSPRAAK'],
  // batch-68 controle (FO-343 §4.1/§4.3).
  PAUZE: [
    'PAUZE_NIET_FACTUREERBAAR',
    'PAUZE_VOLLEDIG_FACTUREERBAAR',
    'PAUZE_PERCENTAGE',
    'PAUZE_MINUTEN_FACTUREERBAAR',
    'PAUZE_MINUTEN_NIET_FACTUREERBAAR',
  ],
  UREN: ['MINIMUM_PER_DAG', 'MAXIMUM_PER_DAG', 'AFRONDING'],
}

// ─── DTO (server → client) ────────────────────────────────────────────────

export interface ClientAgreementRuleDTO {
  id: string
  clientId: string
  category: AgreementCategory
  ruleType: AgreementRuleType

  employeeId: string | null
  werkbonType: WerkbonType | null
  dienstType: DienstType | null
  ploegSoort: PloegSoort | null

  /** Decimal komt over de wire als string — UI parset waar nodig. */
  value: string | null
  value2: string | null
  textValue: string | null
  flag: boolean

  description: string | null
  priority: number
  validFrom: string | null
  validUntil: string | null
  isActive: boolean

  // batch-68 controle (FO-343 §4.2/§4.4/§4.5).
  niveau: BegrenzingNiveau | null
  functieGroep: string | null
  projectId: string | null
  /** Leesbaar label van het project (nummer + naam), alleen in de lijst. */
  projectLabel?: string | null
  versie: number

  createdAt: string
  updatedAt: string

  /** Optioneel uitgeklapte employee voor de UI (lijst-weergave). */
  employee?: {
    id: string
    firstName: string
    lastName: string
  } | null
}

// ─── Resolver-output (spec §4.2) ──────────────────────────────────────────

export interface ResolvedAgreementValue {
  description: string
  value: string | null
}

export interface ResolvedAgreements {
  /** Reistijd wordt al door het uurtarief gedekt — niet apart factureren. */
  reistijdInbegrepen: boolean
  /** Reiskilometers worden al door het uurtarief gedekt. */
  reiskostenInbegrepen: boolean
  /** Maximum aantal te factureren km's — null = geen maximum. */
  maxReisKm: number | null
  /** Maximum aantal te factureren reisminuten — null = geen maximum. */
  maxReisMinuten: number | null

  /**
   * §6.2 — true ⇒ geen drempel-aftrek voor deze klant (bruto-reistijd
   * volledig vergoedbaar). False/undefined ⇒ standaard drempel-logica.
   * Bron: rule(category=REISTIJD, ruleType=REISTIJD_BRUTO).
   */
  reistijdBruto?: boolean
  /**
   * §6.2 — custom drempel in minuten die wordt afgetrokken van berekende
   * reistijd vóór vergoedbaarheid. Override van globale
   * `REISUREN_DREMPEL_MINUTEN`. Null/undefined ⇒ gebruik globale default.
   * Bron: rule(category=REISTIJD, ruleType=REISTIJD_DREMPEL).
   * Wordt genegeerd wanneer `reistijdBruto === true`.
   */
  reistijdDrempelMinuten?: number | null

  /**
   * §5.1 #1 — Afwijkend uurtarief voor deze klant/scope; null = gebruik
   * standaard ClientTariff. Functioneert als override: dit IS de
   * `uurtariefOverride` die werkbon-facturatie inleest.
   * Bron: rule(category=UURTARIEF, ruleType=AFWIJKEND_TARIEF).
   */
  uurtarief: string | null
  /**
   * §5.1 #1 — Afwijkend overuurtarief; override van standaard ClientTariff.
   * Bron: rule(category=OVERUURTARIEF, ruleType=AFWIJKEND_TARIEF).
   */
  overuurtarief: string | null

  ploegentoeslag: {
    type: 'percentage' | 'bedrag'
    value: string
  } | null

  dagvergoeding: string | null
  weekvergoeding: string | null

  consignatieStandbyTarief: string | null
  consignatieOproepTarief: string | null
  consignatieMinimumUren: string | null

  /** Override van km-vergoeding. */
  reisKmTarief: string | null
  /** Override van minuut-vergoeding voor reistijd. */
  reisMinuutTarief: string | null

  /** Vrije afspraken — alleen documentatief, geen factuur-impact. */
  vrijeAfspraken: ResolvedAgreementValue[]

  /**
   * batch-68 controle (FO-343 §4) - de afspraken van de motor. Afwezig of null =
   * geen enkele motorregel: de facturatie werkt dan exact als voorheen.
   */
  motor?: ResolvedMotorAfspraken | null
}

/** batch-68 controle - verwijzing naar de winnende afspraak (id + versie, voor het spoor en het snapshot). */
export interface ResolvedAfspraakRef {
  ruleId: string
  versie: number
}

export interface ResolvedMotorAfspraken {
  pauze: (ResolvedAfspraakRef & { soort: PauzeRuleType; waarde: number | null }) | null
  afronding: (ResolvedAfspraakRef & { eenheidMinuten: number; modus: AfrondingModusType; niveau: 'DAG' | 'REGEL' }) | null
  minimumPerDag: (ResolvedAfspraakRef & { uren: number }) | null
  maximumPerDag: (ResolvedAfspraakRef & { uren: number }) | null
  vastePrijs:
    | (ResolvedAfspraakRef & { bedrag: number; inbegrepenUren: number | null; meerwerkToegestaan: boolean; projectId: string | null })
    | null
  reistijdPercentage: (ResolvedAfspraakRef & { pct: number }) | null
  /** REISTIJD-MAXIMUM per niveau (één winnaar per niveau). */
  reistijdMaxima: Array<ResolvedAfspraakRef & { niveau: ReistijdBegrenzingNiveau; maxMinuten: number }>
}

// ─── Zod-schemas (input-validatie aan server-zijde) ───────────────────────

const werkbonTypeEnum = z.enum(['ONDERHOUD', 'SERVICE', 'PROJECT'])
const dienstTypeEnum = z.enum(['DAGDIENST', 'PLOEGEN_2', 'PLOEGEN_3', 'PLOEGEN_4', 'PLOEGEN_5'])
const ploegSoortEnum = z.enum(['DAG', 'VROEG', 'LAAT', 'NACHT'])

export const agreementCategoryEnum = z.enum(AGREEMENT_CATEGORIES)
export const agreementRuleTypeEnum = z.enum(AGREEMENT_RULE_TYPES)

export const createAgreementRuleSchema = z.object({
  category: agreementCategoryEnum,
  ruleType: agreementRuleTypeEnum,

  employeeId: z.string().min(1).optional().nullable(),
  werkbonType: werkbonTypeEnum.optional().nullable(),
  dienstType: dienstTypeEnum.optional().nullable(),
  ploegSoort: ploegSoortEnum.optional().nullable(),

  value: z.number().finite().optional().nullable(),
  value2: z.number().finite().optional().nullable(),
  textValue: z.string().max(2000).optional().nullable(),
  flag: z.boolean().optional(),

  description: z.string().max(500).optional().nullable(),
  priority: z.number().int().min(-100).max(1000).optional(),
  validFrom: z.string().datetime().optional().nullable(),
  validUntil: z.string().datetime().optional().nullable(),
  isActive: z.boolean().optional(),

  // batch-68 controle (FO-343 §4.2/§4.4/§4.5).
  niveau: z.enum(BEGRENZING_NIVEAUS).optional().nullable(),
  functieGroep: z.string().trim().max(120).optional().nullable(),
  projectId: z.string().min(1).optional().nullable(),
  /** Optionele reden voor de audit van de wijziging. */
  reden: z.string().trim().max(2000).optional().nullable(),
})

export type CreateAgreementRuleInput = z.infer<typeof createAgreementRuleSchema>

export const updateAgreementRuleSchema = createAgreementRuleSchema.partial()
export type UpdateAgreementRuleInput = z.infer<typeof updateAgreementRuleSchema>

export const listAgreementRulesQuerySchema = z.object({
  category: agreementCategoryEnum.optional(),
  isActive: z
    .union([z.literal('true'), z.literal('false')])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true')),
})

export const resolveAgreementsQuerySchema = z.object({
  employeeId: z.string().min(1).optional(),
  werkbonType: werkbonTypeEnum.optional(),
  dienstType: dienstTypeEnum.optional(),
  ploegSoort: ploegSoortEnum.optional(),
  date: z.string().datetime().optional(),
})

export type ResolveAgreementsQuery = z.infer<typeof resolveAgreementsQuerySchema>

/**
 * Per-ruleType validatie van de minimaal vereiste velden.
 * Wordt door de POST-route uitgevoerd bovenop het Zod-schema — Zod kan dit
 * niet zelf omdat de regel afhangt van combinatie category × ruleType.
 *
 * Spec §5.3 omschrijft welke formuliervelden per ruleType verplicht zijn.
 */
export function validateRuleShape(input: {
  ruleType: AgreementRuleType
  value: number | null | undefined
  textValue: string | null | undefined
  flag?: boolean
  /** batch-68 controle - het niveau (REISTIJD-MAXIMUM en AFRONDING). */
  niveau?: BegrenzingNiveau | null
  value2?: number | null | undefined
  category?: AgreementCategory
}): { ok: true } | { ok: false; error: string } {
  const { ruleType, value, textValue } = input

  // batch-68 controle (FO-343 §4) - de motorregels.
  const motor = validateMotorRuleShape(input)
  if (motor) return motor

  switch (ruleType) {
    case 'INBEGREPEN':
      // Spec §5.3: geen extra velden. `flag` wordt aan server-zijde geforceerd op true.
      return { ok: true }

    // §6.2 — REISTIJD_BRUTO is een boolean-rule (flag-based). De server
    // forceert `flag = true` net als bij INBEGREPEN; value is niet vereist.
    case 'REISTIJD_BRUTO':
      return { ok: true }

    case 'MAXIMUM':
    case 'VAST_TARIEF_PER_EENHEID':
    case 'AFWIJKEND_TARIEF':
    case 'TOESLAG_PERCENTAGE':
    case 'TOESLAG_BEDRAG':
    case 'DAGVERGOEDING':
    case 'WEEKVERGOEDING':
    case 'STANDBY_WEEKTARIEF':
    case 'STANDBY_DAGTARIEF':
    case 'OPROEP_UURTARIEF':
    case 'OPROEP_MINIMUMUREN':
    // §6.2 — REISTIJD_DREMPEL: value = drempel-minuten. Validatie identiek aan
    // andere numeric rules (≥ 0, vereist).
    case 'REISTIJD_DREMPEL':
      if (value === null || value === undefined) {
        return {
          ok: false,
          error: `Regeltype ${ruleType} vereist een numerieke waarde`,
        }
      }
      if (value < 0) {
        return {
          ok: false,
          error: 'Waarde mag niet negatief zijn',
        }
      }
      return { ok: true }

    case 'VRIJE_AFSPRAAK':
      if (!textValue || textValue.trim().length === 0) {
        return {
          ok: false,
          error: 'Vrije afspraak vereist een omschrijving',
        }
      }
      return { ok: true }

    default:
      // Exhaustiveness check — TypeScript-only.
      return { ok: true }
  }
}

/**
 * batch-68 controle (FO-343 §4) - de vormcontrole van de motorregels. Geeft
 * `null` voor een regelsoort die hier niet thuishoort (de oude switch doet die).
 */
function validateMotorRuleShape(input: {
  ruleType: AgreementRuleType
  value: number | null | undefined
  textValue: string | null | undefined
  niveau?: BegrenzingNiveau | null
  value2?: number | null | undefined
  category?: AgreementCategory
}): { ok: true } | { ok: false; error: string } | null {
  const { ruleType, value, textValue, niveau, value2 } = input
  const vereist = (min: number, max: number, wat: string): { ok: true } | { ok: false; error: string } => {
    if (value === null || value === undefined) return { ok: false, error: `${wat}: vul een waarde in` }
    if (value < min || value > max) return { ok: false, error: `${wat}: kies een waarde van ${min} tot en met ${max}` }
    return { ok: true }
  }
  switch (ruleType) {
    case 'PAUZE_NIET_FACTUREERBAAR':
    case 'PAUZE_VOLLEDIG_FACTUREERBAAR':
      return { ok: true }
    case 'PAUZE_PERCENTAGE':
      return vereist(0, 100, 'Percentage van de pauze')
    case 'PAUZE_MINUTEN_FACTUREERBAAR':
    case 'PAUZE_MINUTEN_NIET_FACTUREERBAAR':
      return vereist(0, 240, 'Minuten per dag')
    case 'REISTIJD_PERCENTAGE':
      return vereist(0, 100, 'Percentage van de reistijd')
    case 'MINIMUM_PER_DAG':
    case 'MAXIMUM_PER_DAG':
      return vereist(0.25, 24, 'Uren per dag')
    case 'AFRONDING': {
      if (value !== 15 && value !== 30 && value !== 60) return { ok: false, error: 'Afronding: kies 15, 30 of 60 minuten' }
      if (textValue !== 'OMHOOG' && textValue !== 'DICHTSTBIJ' && textValue !== 'OMLAAG') {
        return { ok: false, error: 'Afronding: kies naar boven, naar de dichtstbijzijnde of naar beneden' }
      }
      if (niveau !== 'DAG' && niveau !== 'REGEL') return { ok: false, error: 'Afronding: kies per dag of per factuurregel' }
      return { ok: true }
    }
    case 'VASTE_PRIJS_PERIODE': {
      const v = vereist(0.01, 10_000_000, 'Vaste prijs per periode')
      if (!v.ok) return v
      if (value2 !== null && value2 !== undefined && value2 < 0) return { ok: false, error: 'Inbegrepen uren mogen niet negatief zijn' }
      return { ok: true }
    }
    case 'MAXIMUM':
      // Een REISTIJD-MAXIMUM met niveau: REGEL is geen reistijdniveau.
      if (input.category === 'REISTIJD' && niveau === 'REGEL') {
        return { ok: false, error: 'Kies een niveau per rit, dag, week, periode, werkbon of project' }
      }
      return null
    default:
      return null
  }
}
